import React, { useState } from 'react';
import { PersonEntry, PersonData } from './PersonEntry';
import { InvitationPreview } from './InvitationPreview';
import { CheckCircle2Icon, CheckIcon, ChevronRightIcon, ChevronLeftIcon, HeartIcon, UsersIcon, ShieldCheckIcon, RocketIcon, PlusIcon, SmartphoneIcon, SparklesIcon, MessageSquareIcon, CopyIcon, ArrowDownIcon } from 'lucide-react';
const STEPS = [{
  id: 'details',
  title: 'Wedding Details',
  icon: HeartIcon
}, {
  id: 'whatsapp',
  title: 'WhatsApp Guide',
  icon: SmartphoneIcon
}, {
  id: 'people',
  title: 'Add Family',
  icon: UsersIcon
}, {
  id: 'permission',
  title: 'Permissions',
  icon: ShieldCheckIcon
}, {
  id: 'launch',
  title: 'Launch',
  icon: RocketIcon
}];
export function KickoffWizard() {
  const [currentStep, setCurrentStep] = useState(0);
  const [showInvitePreview, setShowInvitePreview] = useState(false);
  // State: Wedding Details
  const [details, setDetails] = useState({
    coupleNames: '',
    weddingTitle: '',
    destination: '',
    dates: '',
    subtitle: '',
    link: '',
    welcomeNote: ''
  });
  // State: People
  const [people, setPeople] = useState<PersonData[]>([{
    id: '1',
    fullName: '',
    mobile: '',
    email: '',
    relationship: '',
    connection: '',
    notes: ''
  }]);
  // State: Permissions
  const [permissions, setPermissions] = useState({
    share: false,
    use: false,
    connected: false
  });
  const handleDetailChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setDetails({
      ...details,
      [e.target.name]: e.target.value
    });
  };
  // Unified wedding data — selecting a wedding auto-populates everything
  const weddingData = [{
    couple: 'Priya & Rohan',
    destination: 'Cancun, Mexico',
    dates: 'Nov 12–15, 2025',
    title: '#PriyaGotRoed',
    link: 'https://destapass.com/join/priya-rohan-cancun'
  }, {
    couple: 'Ananya & Vikram',
    destination: 'Udaipur, India',
    dates: 'Dec 5–8, 2025',
    title: '#AnanyaWedVikram',
    link: 'https://destapass.com/join/ananya-vikram-udaipur'
  }, {
    couple: 'Simran & Arjun',
    destination: 'Bali, Indonesia',
    dates: 'Jan 10–13, 2026',
    title: '#SimArjForever',
    link: 'https://destapass.com/join/simran-arjun-bali'
  }, {
    couple: 'Neha & Karan',
    destination: 'Punta Cana, Dominican Republic',
    dates: 'Feb 14–17, 2026',
    title: '#NehaKaranKiShaadi',
    link: 'https://destapass.com/join/neha-karan-puntacana'
  }, {
    couple: 'Pooja & Raj',
    destination: 'Goa, India',
    dates: 'Mar 20–23, 2026',
    title: '#PoojaRajVivaah',
    link: 'https://destapass.com/join/pooja-raj-goa'
  }, {
    couple: 'Meera & Sahil',
    destination: 'Dubai, UAE',
    dates: 'Apr 8–11, 2026',
    title: '#MeeraSahilMagic',
    link: 'https://destapass.com/join/meera-sahil-dubai'
  }, {
    couple: 'Divya & Aditya',
    destination: 'Tulum, Mexico',
    dates: 'May 15–18, 2026',
    title: '#DivyaAdityaDreams',
    link: 'https://destapass.com/join/divya-aditya-tulum'
  }, {
    couple: 'Riya & Nikhil',
    destination: 'Jaipur, India',
    dates: 'Jun 5–8, 2026',
    title: '#RiyaNikhilRoyale',
    link: 'https://destapass.com/join/riya-nikhil-jaipur'
  }, {
    couple: 'Kavya & Dhruv',
    destination: 'Phuket, Thailand',
    dates: 'Oct 2–5, 2026',
    title: '#KavyaDhruvDestination',
    link: 'https://destapass.com/join/kavya-dhruv-phuket'
  }, {
    couple: 'Ishita & Manish',
    destination: 'Riviera Maya, Mexico',
    dates: 'Nov 20–23, 2026',
    title: '#IshitaManishMaya',
    link: 'https://destapass.com/join/ishita-manish-riviera'
  }];
  const handleWeddingSelect = (coupleName: string) => {
    if (!coupleName) {
      setDetails({
        coupleNames: '',
        weddingTitle: '',
        destination: '',
        dates: '',
        subtitle: '',
        link: '',
        welcomeNote: details.welcomeNote
      });
      return;
    }
    const wedding = weddingData.find(w => w.couple === coupleName);
    if (wedding) {
      setDetails({
        ...details,
        coupleNames: wedding.couple,
        destination: wedding.destination,
        dates: wedding.dates,
        weddingTitle: wedding.title,
        link: wedding.link
      });
    }
  };
  const handlePersonChange = (id: string, field: keyof PersonData, value: string) => {
    setPeople(people.map(p => p.id === id ? {
      ...p,
      [field]: value
    } : p));
  };
  const addPerson = () => {
    setPeople([...people, {
      id: Math.random().toString(36).substr(2, 9),
      fullName: '',
      mobile: '',
      email: '',
      relationship: '',
      connection: '',
      notes: ''
    }]);
  };
  const removePerson = (id: string) => {
    if (people.length > 1) {
      setPeople(people.filter(p => p.id !== id));
    }
  };
  const nextStep = () => setCurrentStep(Math.min(currentStep + 1, STEPS.length - 1));
  const prevStep = () => setCurrentStep(Math.max(currentStep - 1, 0));
  const isStepValid = () => {
    if (currentStep === 0) {
      return details.coupleNames && details.destination && details.link;
    }
    if (currentStep === 2) {
      return people.every(p => p.fullName && p.mobile && p.relationship && p.connection);
    }
    if (currentStep === 3) {
      return permissions.share && permissions.use && permissions.connected;
    }
    return true;
  };
  // Check if enough details are filled to show a live preview
  const hasEnoughForPreview = details.coupleNames && details.destination;
  return <div className="bg-white rounded-3xl shadow-xl border border-amber-100 overflow-hidden" data-id="element-436">
      {/* Progress Header */}
      <div className="bg-gradient-to-r from-amber-50 to-rose-50 p-4 md:p-6 border-b border-amber-100" data-id="element-437">
        <div className="flex items-center justify-between max-w-4xl mx-auto relative" data-id="element-438">
          {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isActive = idx === currentStep;
          const isPast = idx < currentStep;
          return <div key={step.id} className="flex flex-col items-center relative z-10" data-id="element-439">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${isActive ? 'bg-rose-500 text-white shadow-lg scale-110' : isPast ? 'bg-amber-200 text-amber-700' : 'bg-white text-gray-300 border-2 border-gray-100'}`} data-id="element-440">
                  {isPast ? <CheckCircle2Icon className="h-5 w-5" data-id="element-441" /> : <Icon className="h-5 w-5" data-id="element-442" />}
                </div>
                <span className={`text-[10px] md:text-xs font-medium mt-2 hidden md:block ${isActive ? 'text-rose-600' : isPast ? 'text-amber-700' : 'text-gray-400'}`} data-id="element-443">
                  {step.title}
                </span>
              </div>;
        })}
          {/* Progress Line */}
          <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-100 -z-0 mx-10" data-id="element-444">
            <div className="h-full bg-gradient-to-r from-amber-300 to-rose-400 transition-all duration-500" style={{
            width: `${currentStep / (STEPS.length - 1) * 100}%`
          }} data-id="element-445" />
          </div>
        </div>
      </div>

      {/* Step Content */}
      <div className="p-6 md:p-10 min-h-[400px]" data-id="element-446">
        {/* STEP 0: WEDDING DETAILS + LIVE INVITE PREVIEW */}
        {currentStep === 0 && <div className="max-w-4xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-447">
            <div className="text-center mb-8" data-id="element-448">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2" data-id="element-449">
                Wedding Details
              </h2>
              <p className="text-gray-500" data-id="element-450">
                Fill in your details and watch your welcome invitation come to
                life below.
              </p>
            </div>

            <div className="grid lg:grid-cols-2 gap-8" data-id="element-451">
              {/* Left: Form Fields */}
              <div className="space-y-5" data-id="element-452">
                <div data-id="element-453">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-454">
                    Select Wedding *
                  </label>
                  <select value={details.coupleNames} onChange={e => handleWeddingSelect(e.target.value)} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none bg-white" data-id="element-455">
                    <option value="" data-id="element-456">Choose the couple...</option>
                    {weddingData.map(w => <option key={w.couple} value={w.couple} data-id="element-457">
                        {w.couple}
                      </option>)}
                  </select>
                </div>

                {/* Auto-populated fields */}
                {details.coupleNames && <div className="bg-amber-50/60 rounded-2xl p-5 border border-amber-100 space-y-4" data-id="element-458">
                    <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1" data-id="element-459">
                      Auto-filled from wedding selection
                    </p>
                    <div className="grid grid-cols-2 gap-4" data-id="element-460">
                      <div data-id="element-461">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-462">
                          Destination
                        </label>
                        <div className="px-4 py-2.5 bg-white rounded-xl border border-amber-200 text-gray-800 font-medium text-sm" data-id="element-463">
                          {details.destination}
                        </div>
                      </div>
                      <div data-id="element-464">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-465">
                          Dates
                        </label>
                        <div className="px-4 py-2.5 bg-white rounded-xl border border-amber-200 text-gray-800 font-medium text-sm" data-id="element-466">
                          {details.dates}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4" data-id="element-467">
                      <div data-id="element-468">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-469">
                          Wedding Title
                        </label>
                        <div className="px-4 py-2.5 bg-white rounded-xl border border-amber-200 text-gray-800 font-medium text-sm" data-id="element-470">
                          {details.weddingTitle}
                        </div>
                      </div>
                      <div data-id="element-471">
                        <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-472">
                          Circle Link
                        </label>
                        <div className="px-4 py-2.5 bg-white rounded-xl border border-amber-200 text-rose-600 font-medium text-sm truncate" data-id="element-473">
                          {details.link}
                        </div>
                      </div>
                    </div>
                  </div>}

                <div data-id="element-474">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-475">
                    Personal Note{' '}
                    <span className="text-gray-400 font-normal" data-id="element-476">
                      (Optional)
                    </span>
                  </label>
                  <textarea name="welcomeNote" value={details.welcomeNote} onChange={handleDetailChange} rows={2} placeholder="Add a personal touch..." className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:border-rose-400 focus:ring-2 focus:ring-rose-100 outline-none resize-none" data-id="element-477" />
                </div>
              </div>

              {/* Right: Live Invitation Preview */}
              <div className="bg-gradient-to-br from-amber-50 to-rose-50 rounded-2xl p-5 border border-amber-100" data-id="element-478">
                <div className="flex items-center gap-2 mb-4" data-id="element-479">
                  <MessageSquareIcon className="h-4 w-4 text-rose-500" data-id="element-480" />
                  <span className="text-sm font-semibold text-gray-700" data-id="element-481">
                    Live Invitation Preview
                  </span>
                </div>

                {hasEnoughForPreview ? <>
                    {/* Mini WhatsApp bubble */}
                    <div className="bg-[#EFEAE2] rounded-2xl p-3 shadow-inner" data-id="element-482">
                      <div className="bg-[#00A884] text-white p-2.5 -mx-3 -mt-3 mb-3 rounded-t-2xl flex items-center gap-2" data-id="element-483">
                        <div className="w-6 h-6 bg-white/20 rounded-full" data-id="element-484"></div>
                        <span className="text-xs font-medium" data-id="element-485">
                          Family Group
                        </span>
                      </div>
                      <div className="bg-white rounded-xl rounded-tl-none p-3 shadow-sm" data-id="element-486">
                        <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap" data-id="element-487">
                          {`We are so excited to celebrate our wedding with you! 💍✨\n\nWhile we wish everyone could join us in ${details.destination || '[Destination]'}, we know a destination wedding makes that difficult. But we couldn't imagine celebrating without you being a part of it.\n\nWe've set up a private wedding community on Desta Pass so you can follow our journey, see live photos, and feel like you're right there with us!${details.welcomeNote ? `\n\n"${details.welcomeNote}"` : ''}\n\nPlease join here:\n${details.link || 'https://destapass.com/join/...'}\n\nWith love,\n${details.coupleNames || '[Couple Names]'}`}
                        </p>
                        <div className="text-[9px] text-gray-400 text-right mt-1" data-id="element-488">
                          10:42 AM
                        </div>
                      </div>
                    </div>

                    <button onClick={() => setShowInvitePreview(true)} className="mt-4 w-full py-2.5 text-sm font-medium text-rose-600 bg-white border border-rose-200 rounded-xl hover:bg-rose-50 transition-colors flex items-center justify-center gap-2" data-id="element-489">
                      <CopyIcon className="h-4 w-4" data-id="element-490" />
                      Customize & Copy Invite
                    </button>
                  </> : <div className="flex flex-col items-center justify-center py-12 text-center" data-id="element-491">
                    <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mb-4" data-id="element-492">
                      <ArrowDownIcon className="h-6 w-6 text-amber-500 animate-bounce" data-id="element-493" />
                    </div>
                    <p className="text-sm text-gray-500 max-w-[200px]" data-id="element-494">
                      Start filling in your details to see your invitation
                      appear here
                    </p>
                  </div>}
              </div>
            </div>

            {/* Full Invitation Editor Modal/Expanded */}
            {showInvitePreview && <div className="mt-8 bg-white rounded-2xl border border-amber-200 p-6 shadow-lg" data-id="element-495">
                <div className="flex items-center justify-between mb-4" data-id="element-496">
                  <h3 className="text-lg font-bold text-gray-800" data-id="element-497">
                    Customize Your Invitation
                  </h3>
                  <button onClick={() => setShowInvitePreview(false)} className="text-gray-400 hover:text-gray-600 text-sm font-medium" data-id="element-498">
                    Close
                  </button>
                </div>
                <InvitationPreview coupleNames={details.coupleNames} weddingTitle={details.weddingTitle} destination={details.destination} link={details.link} customNote={details.welcomeNote} data-id="element-499" />
              </div>}
          </div>}

        {/* STEP 1: WHATSAPP GUIDE (now before Add Family) */}
        {currentStep === 1 && <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-500">
            <div className="text-center mb-8" data-id="element-501">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2" data-id="element-502">
                Adding People is Easy
              </h2>
              <p className="text-gray-500" data-id="element-503">
                In the next step, you'll add your family and guests. Here's a
                quick tip on how to find their contact details — it only takes
                seconds.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl p-6 md:p-8 border border-green-100 mb-8" data-id="element-504">
              <h3 className="font-bold text-gray-800 text-lg mb-6 flex items-center gap-2" data-id="element-505">
                <SmartphoneIcon className="h-5 w-5 text-green-600" data-id="element-506" />
                Copy numbers straight from WhatsApp
              </h3>
              <div className="space-y-5" data-id="element-507">
                <div className="flex items-start gap-4" data-id="element-508">
                  <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0 shadow-md" data-id="element-509">
                    1
                  </div>
                  <div data-id="element-510">
                    <h4 className="font-bold text-gray-800" data-id="element-511">
                      Open any WhatsApp chat
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-512">
                      Find the family member in your chats or a family group.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-513">
                  <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0 shadow-md" data-id="element-514">
                    2
                  </div>
                  <div data-id="element-515">
                    <h4 className="font-bold text-gray-800" data-id="element-516">
                      Tap their name at the top
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-517">
                      This opens their contact info where you'll see their phone
                      number.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-id="element-518">
                  <div className="w-10 h-10 rounded-full bg-green-500 text-white flex items-center justify-center font-bold flex-shrink-0 shadow-md" data-id="element-519">
                    3
                  </div>
                  <div data-id="element-520">
                    <h4 className="font-bold text-gray-800" data-id="element-521">
                      Long-press to copy the number
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-522">
                      Then paste it into the form on the next step. That's it!
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Reassurance cards */}
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-523">
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-id="element-524">
                <div className="text-2xl mb-2" data-id="element-525">⏱️</div>
                <h4 className="font-bold text-gray-800 mb-1" data-id="element-526">
                  Takes 30 seconds per person
                </h4>
                <p className="text-sm text-gray-500" data-id="element-527">
                  Just a name, number, and relationship — that's all you need.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-id="element-528">
                <div className="text-2xl mb-2" data-id="element-529">📱</div>
                <h4 className="font-bold text-gray-800 mb-1" data-id="element-530">
                  No app download needed
                </h4>
                <p className="text-sm text-gray-500" data-id="element-531">
                  Your guests will receive a simple link to join the wedding
                  community.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-id="element-532">
                <div className="text-2xl mb-2" data-id="element-533">👨‍👩‍👧‍👦</div>
                <h4 className="font-bold text-gray-800 mb-1" data-id="element-534">
                  Add as many as you like
                </h4>
                <p className="text-sm text-gray-500" data-id="element-535">
                  Start with a few key people now. You can always add more
                  later.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm" data-id="element-536">
                <div className="text-2xl mb-2" data-id="element-537">📧</div>
                <h4 className="font-bold text-gray-800 mb-1" data-id="element-538">
                  Email is optional
                </h4>
                <p className="text-sm text-gray-500" data-id="element-539">
                  Phone numbers are all you need. Most families already have
                  them in WhatsApp.
                </p>
              </div>
            </div>
          </div>}

        {/* STEP 2: ADD PEOPLE */}
        {currentStep === 2 && <div className="max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-540">
            <div className="text-center mb-8" data-id="element-541">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2" data-id="element-542">
                Add Family & Guests
              </h2>
              <p className="text-gray-500" data-id="element-543">
                Add the loved ones you want to invite into your wedding
                community.
              </p>
            </div>

            <div className="space-y-6" data-id="element-544">
              {people.map((person, index) => <PersonEntry key={person.id} person={person} index={index} onChange={handlePersonChange} onRemove={removePerson} data-id="element-545" />)}
            </div>

            <div className="mt-6 flex justify-center" data-id="element-546">
              <button onClick={addPerson} className="flex items-center gap-2 px-6 py-3 bg-amber-50 text-amber-700 rounded-full font-medium hover:bg-amber-100 transition-colors border border-amber-200" data-id="element-547">
                <PlusIcon className="h-5 w-5" data-id="element-548" />
                Add Another Person
              </button>
            </div>

            <p className="text-center text-sm text-gray-400 mt-4" data-id="element-549">
              Don't have everyone's details yet? No problem — you can always
              come back and add more people later.
            </p>
          </div>}

        {/* STEP 3: PERMISSIONS */}
        {currentStep === 3 && <div className="max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-550">
            <div className="text-center mb-8" data-id="element-551">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2" data-id="element-552">
                Permission & Approval
              </h2>
              <p className="text-gray-500" data-id="element-553">
                Please confirm your authorization to invite these guests.
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 space-y-6 shadow-sm" data-id="element-554">
              <label className="flex items-start gap-4 cursor-pointer group" onClick={() => setPermissions({
            ...permissions,
            share: !permissions.share
          })} data-id="element-555">
                <div className="relative flex items-center justify-center mt-0.5 flex-shrink-0" data-id="element-556">
                  <div className={`w-6 h-6 border-2 rounded-md flex items-center justify-center transition-all duration-200 ${permissions.share ? 'bg-rose-500 border-rose-500' : 'border-gray-300 bg-white'}`} data-id="element-557">
                    {permissions.share && <CheckIcon className="text-white w-4 h-4" data-id="element-558" />}
                  </div>
                </div>
                <span className="text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-559">
                  I confirm that I have permission to share these contact
                  details for wedding communication purposes.
                </span>
              </label>

              <label className="flex items-start gap-4 cursor-pointer group" onClick={() => setPermissions({
            ...permissions,
            use: !permissions.use
          })} data-id="element-560">
                <div className="relative flex items-center justify-center mt-0.5 flex-shrink-0" data-id="element-561">
                  <div className={`w-6 h-6 border-2 rounded-md flex items-center justify-center transition-all duration-200 ${permissions.use ? 'bg-rose-500 border-rose-500' : 'border-gray-300 bg-white'}`} data-id="element-562">
                    {permissions.use && <CheckIcon className="text-white w-4 h-4" data-id="element-563" />}
                  </div>
                </div>
                <span className="text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-564">
                  I understand this information will be used to help invite
                  people into the wedding experience.
                </span>
              </label>

              <label className="flex items-start gap-4 cursor-pointer group" onClick={() => setPermissions({
            ...permissions,
            connected: !permissions.connected
          })} data-id="element-565">
                <div className="relative flex items-center justify-center mt-0.5 flex-shrink-0" data-id="element-566">
                  <div className={`w-6 h-6 border-2 rounded-md flex items-center justify-center transition-all duration-200 ${permissions.connected ? 'bg-rose-500 border-rose-500' : 'border-gray-300 bg-white'}`} data-id="element-567">
                    {permissions.connected && <CheckIcon className="text-white w-4 h-4" data-id="element-568" />}
                  </div>
                </div>
                <span className="text-gray-700 group-hover:text-gray-900 transition-colors" data-id="element-569">
                  I confirm these guests are personally connected to the wedding
                  and family.
                </span>
              </label>
            </div>
          </div>}

        {/* STEP 4: LAUNCH */}
        {currentStep === 4 && <div className="max-w-2xl mx-auto text-center animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-570">
            <div className="w-24 h-24 bg-gradient-to-br from-rose-400 to-amber-400 rounded-full flex items-center justify-center mx-auto mb-8 shadow-xl shadow-rose-200" data-id="element-571">
              <SparklesIcon className="h-12 w-12 text-white" data-id="element-572" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4" data-id="element-573">
              Your wedding community is ready to begin.
            </h2>
            <p className="text-lg text-gray-600 mb-10" data-id="element-574">
              You've started bringing everyone into the celebration. Here is a
              summary of your kickoff:
            </p>

            <div className="bg-gray-50 rounded-2xl p-6 text-left border border-gray-100 mb-10" data-id="element-575">
              <div className="grid grid-cols-2 gap-y-4" data-id="element-576">
                <div className="text-sm text-gray-500" data-id="element-577">Wedding</div>
                <div className="font-medium text-gray-900 text-right" data-id="element-578">
                  {details.coupleNames || 'Not provided'}
                </div>
                <div className="text-sm text-gray-500" data-id="element-579">Destination</div>
                <div className="font-medium text-gray-900 text-right" data-id="element-580">
                  {details.destination || 'Not provided'}
                </div>
                <div className="text-sm text-gray-500" data-id="element-581">
                  Family & Guests Added
                </div>
                <div className="font-medium text-gray-900 text-right" data-id="element-582">
                  {people.filter(p => p.fullName).length} people
                </div>
                <div className="text-sm text-gray-500" data-id="element-583">Community Link</div>
                <div className="font-medium text-rose-600 text-right truncate pl-4" data-id="element-584">
                  {details.link || 'Not provided'}
                </div>
              </div>
            </div>
          </div>}
      </div>

      {/* Footer Navigation */}
      <div className="bg-gray-50 p-4 md:p-6 border-t border-gray-100 flex items-center justify-between" data-id="element-585">
        {currentStep > 0 ? <button onClick={prevStep} className="px-6 py-3 text-gray-600 font-medium hover:bg-gray-200 rounded-xl transition-colors flex items-center gap-2" data-id="element-586">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-587" />
            Back
          </button> : <div data-id="element-588"></div>}

        {currentStep < STEPS.length - 1 ? <button onClick={nextStep} disabled={!isStepValid()} className="px-8 py-3 bg-rose-500 text-white font-semibold rounded-xl hover:bg-rose-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md shadow-rose-200" data-id="element-589">
            Continue
            <ChevronRightIcon className="h-5 w-5" data-id="element-590" />
          </button> : <div className="flex gap-3" data-id="element-591">
            <button className="px-6 py-3 text-rose-600 font-medium hover:bg-rose-50 rounded-xl transition-colors" data-id="element-592">
              Save for later
            </button>
            <button className="px-8 py-3 bg-gradient-to-r from-rose-500 to-amber-500 text-white font-bold rounded-xl hover:from-rose-600 hover:to-amber-600 transition-all shadow-lg shadow-rose-200 flex items-center gap-2" data-id="element-593">
              Launch Wedding Kickoff
              <RocketIcon className="h-5 w-5" data-id="element-594" />
            </button>
          </div>}
      </div>
    </div>;
}